import { Injectable } from '@angular/core';
import { IEntityTypeInfo, ISalesforceDisplayTemplates } from 'datasource-service-lib';
import { Namespace } from 'enums-lib';
import { EntityType, IDataSourceApiService, IEntity, IEntityPickerService, IIconService, INamedEntity } from 'interfaces-lib';
import { IPickerInfo } from 'interfaces-lib';
import { Observable, of, zip } from 'rxjs';
import { map } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';

interface ISearchEntity {
  type: string;
  queryFields: string[];
  iconUrl?: string;
  iconColor?: string;
  display?: ISalesforceDisplayTemplates;
  mapFn(INamedEntity: IEntity): IPickerInfo;
  cast(IEntity: IEntity): IEntity;
}

@Injectable({ providedIn: 'root' })
export class SalesforceEntityPickerService implements IEntityPickerService {
  private apiService: any; // Changed to any to access custom methods
  private iconService: IIconService;
  private searchEntityTypes: ISearchEntity[] = [];
  private initialized = false;

  constructor(serviceProviderServiceFactory: SharedServiceProviderServiceFactory) {
    const sps = serviceProviderServiceFactory.getServiceProviderService(Namespace.salesforce);
    this.apiService = sps.getDataSourceApiService();
    this.iconService = sps.getIconService();

    // Initialize entity types dynamically
    this.initializeEntityTypes();
  }

  private initializeEntityTypes(): void {
    if (this.apiService.getAvailableEntityTypes) {
      this.apiService.getAvailableEntityTypes().subscribe({
        next: (entityTypes: IEntityTypeInfo[]) => {
          console.debug('[SalesforceEntityPicker] received entity types', entityTypes?.map((et) => et.objectName));
          this.searchEntityTypes = entityTypes.map((et) => ({
            type: et.objectName,
            queryFields: et.queryFields?.length ? et.queryFields : ['Name'],
            iconUrl: et.iconUrl,
            iconColor: et.iconColor,
            mapFn: this.buildMapper(et.display, et.iconUrl, et.iconColor),
            cast: (e: IEntity) => e,
            display: et.display,
          }));
          this.initialized = true;
          console.debug('Initialized dynamic entity types:', this.searchEntityTypes.map((e) => e.type));
        },
        error: (error: Error) => {
          console.error('[SalesforceEntityPicker] Failed to load dynamic entity types', error);
          this.searchEntityTypes = [];
          this.initialized = true;
        },
      });
    } else {
      console.warn('[SalesforceEntityPicker] Datasource service does not support dynamic entity types');
      this.searchEntityTypes = [];
      this.initialized = true;
    }
  }

  findMatches(searchTerm: string, searchTypes?: EntityType[]): Observable<IPickerInfo[]> {
    // Wait for initialization to complete
    if (!this.initialized) {
      // Return empty results while initializing, or wait for it
      return new Observable(observer => {
        const checkInit = setInterval(() => {
          if (this.initialized) {
            clearInterval(checkInit);
            this.performSearch(searchTerm, searchTypes).subscribe(observer);
          }
        }, 100);
      });
    }

    return this.performSearch(searchTerm, searchTypes);
  }

  private performSearch(searchTerm: string, searchTypes?: EntityType[]): Observable<IPickerInfo[]> {
    console.debug('[SalesforceEntityPicker] performing search',
      { searchTerm, searchTypes, typesConfigured: this.searchEntityTypes.map((t) => t.type) });
    const obs: Observable<IPickerInfo[]>[] = [];
    this.searchEntityTypes
      .filter((searchEntity) => !searchTypes || searchTypes.includes(searchEntity.type as any))
      .forEach((entityType) => {
        const query$ = this.apiService.query(entityType.type, { searchTerm, count: 20, queryFields: entityType.queryFields });
        const mapper$ = query$.pipe(
          map((entities: IEntity[]) => entities.map((entity) => entityType.mapFn(entityType.cast(entity))))
        );
        obs.push(mapper$);
      });

    if (obs.length === 0) {
      return of([]);
    }

    const queries$ = zip(...obs);

    return queries$.pipe(
      map((resultsByEntityType: any[][]) => {
        const flat: any[] = [];
        resultsByEntityType.forEach((r) => flat.push(...r));

        flat.sort((a: IPickerInfo, b: IPickerInfo) => {
          return this.checkRelevance(b.label, searchTerm) - this.checkRelevance(a.label, searchTerm);
        });

        return flat;
      })
    );
  }

  private checkRelevance(str: string, searchTerm: string): number {
    return str.toLowerCase().includes(searchTerm.toLowerCase()) ? 1 : 0;
  }

  // TODO: BW - maybe add a 2nd argument and check the types match?
  private static checkedCast<T extends IEntity>(e: IEntity): T {
    return e as T;
  }

  private buildMapper(display?: ISalesforceDisplayTemplates, iconUrl?: string, iconColor?: string): (e: IEntity) => IPickerInfo {
    return (entity: IEntity) => {
      const label = this.renderTemplate(display?.primary, entity) || this.getFallbackLabel(entity);
      const details = this.renderTemplate(display?.secondary, entity);
      const normalizedEntity = { ...entity, name: label } as INamedEntity;
      return {
        entity: normalizedEntity,
        icon: this.iconService.getIcon(entity.type),
        iconUrl,
        iconColor,
        label,
        showId: true,
        details: details || undefined,
      };
    };
  }

  private renderTemplate(template: string | undefined, entity: IEntity): string {
    if (!template?.trim()) {
      return '';
    }
    return template.replace(/{{\s*([^}]+)\s*}}/g, (_, expr) => {
      const value = this.resolvePath(entity, expr.trim());
      return value === undefined || value === null ? '' : String(value);
    }).trim();
  }

  private resolvePath(entity: IEntity, path: string): any {
    if (!path) {
      return undefined;
    }
    return path.split('.').reduce((acc: any, part: string) => {
      if (acc === undefined || acc === null) {
        return undefined;
      }
      return acc[part];
    }, entity as any);
  }

  private getFallbackLabel(entity: IEntity): string {
    const anyEntity = entity as any;
    const candidate = anyEntity.Name || anyEntity.name || entity.id;
    return candidate ? String(candidate) : 'Unknown';
  }
}
